import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { buildInsideSalesSalesOrderEditPath } from "@/lib/hubEntityDetailPaths";
import { cn } from "@/lib/utils";
import {
  fetchUninvoicedOpenSalesOrdersReport,
  type UninvoicedOpenSalesOrderLineRow,
} from "./uninvoicedOpenSalesOrdersApi";

const REPORT_TABS = [
  {
    id: "uninvoiced-open-sales-orders",
    label: "Uninvoiced open sales orders",
  },
] as const;

type ReportTabId = (typeof REPORT_TABS)[number]["id"];

const HELP_TEXT = [
  "Accounting reports sourced from hub Postgres.",
  "",
  "Uninvoiced open sales orders lists open header status (not cancelled, pending, or closed) with line-level uninvoiced quantity.",
  "Uninvoiced quantity subtracts linked GOSI invoice_item quantities by SKU using FIFO on sales order line id.",
  "Ship-by shows sgs_ship_by_date only; blank when that field is unset (not order submitted date).",
].join("\n");

function formatMoney(value: number | string): string {
  const amount = typeof value === "number" ? value : Number.parseFloat(value);
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
}

function formatShipBy(isoDate: string | null): string {
  if (isoDate == null || isoDate.trim() === "") {
    return "—";
  }
  const parsed = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return isoDate;
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

type SortKey =
  | "ship_by_date"
  | "order_number"
  | "client_name"
  | "sku"
  | "uninvoiced_quantity"
  | "quantity"
  | "unit_price"
  | "uninvoiced_value";

const UNINVOICED_OPEN_SO_COLUMNS: HubDataTableColumn<UninvoicedOpenSalesOrderLineRow, SortKey>[] = [
  {
    id: "ship_by_date",
    header: "Ship by",
    sortKey: "ship_by_date",
    overflow: "nowrap",
    sortValue: (row) => row.ship_by_date ?? "",
    cell: (row) => formatShipBy(row.ship_by_date),
  },
  {
    id: "order_number",
    header: "Order",
    sortKey: "order_number",
    sortValue: (row) => row.order_number,
    cell: (row) => (
      <Link
        to={buildInsideSalesSalesOrderEditPath(row.order_number)}
        className="font-medium text-blue-700 hover:text-blue-900"
      >
        {row.order_number}
      </Link>
    ),
  },
  {
    id: "client_name",
    header: "Client",
    sortKey: "client_name",
    sortValue: (row) => row.client_name ?? "",
    cell: (row) => row.client_name ?? "—",
  },
  {
    id: "sku",
    header: "SKU",
    sortKey: "sku",
    cellClassName: "font-mono text-xs text-slate-800",
    sortValue: (row) => row.sku,
    cell: (row) => row.sku,
  },
  {
    id: "uninvoiced_quantity",
    header: "Uninvoiced qty",
    sortKey: "uninvoiced_quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.uninvoiced_quantity,
    cell: (row) => row.uninvoiced_quantity,
  },
  {
    id: "quantity",
    header: "Order qty",
    sortKey: "quantity",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => row.quantity,
    cellClassName: "text-slate-600",
    cell: (row) => row.quantity,
  },
  {
    id: "unit_price",
    header: "Unit price",
    sortKey: "unit_price",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number.parseFloat(row.unit_price),
    cell: (row) => formatMoney(row.unit_price),
  },
  {
    id: "uninvoiced_value",
    header: "Uninvoiced $",
    sortKey: "uninvoiced_value",
    align: "right",
    overflow: "nowrap",
    sortValue: (row) => Number.parseFloat(row.uninvoiced_value),
    cellClassName: "font-medium",
    cell: (row) => formatMoney(row.uninvoiced_value),
  },
];

function UninvoicedOpenSalesOrdersReportPanel(): JSX.Element {
  const reportQuery = useQuery({
    queryKey: ["accounting", "reports", "uninvoiced-open-sales-orders"],
    queryFn: fetchUninvoicedOpenSalesOrdersReport,
    staleTime: 60_000,
  });

  const reportRows = reportQuery.data?.rows ?? [];

  const shippingUninvoiced = useMemo(
    () =>
      (reportQuery.data?.rows ?? [])
        .filter((row) => row.sku.toLowerCase().startsWith("shipping"))
        .reduce((sum, row) => sum + Number.parseFloat(row.uninvoiced_value), 0),
    [reportQuery.data?.rows],
  );

  const productUninvoiced = (reportQuery.data?.total ?? 0) - shippingUninvoiced;

  if (reportQuery.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Loading report…
      </div>
    );
  }

  if (reportQuery.isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {reportQuery.error instanceof Error
          ? reportQuery.error.message
          : "Could not load uninvoiced open sales orders report."}
      </div>
    );
  }

  const report = reportQuery.data!;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total uninvoiced</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 text-2xl font-semibold tabular-nums text-slate-900">
            {formatMoney(report.total)}
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Open orders with balance</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 text-2xl font-semibold tabular-nums text-slate-900">
            {report.ordersWithUninvoiced}
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Detail lines</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 text-2xl font-semibold tabular-nums text-slate-900">
            {report.rowCount}
          </CardBody>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Product / shipping split</CardTitle>
          </CardHeader>
          <CardBody className="pt-0 text-sm text-slate-800">
            <div className="tabular-nums">Product {formatMoney(productUninvoiced)}</div>
            <div className="tabular-nums text-slate-600">Shipping {formatMoney(shippingUninvoiced)}</div>
          </CardBody>
        </Card>
      </div>

      <p className="text-sm text-slate-600">{report.allocation}</p>

      <HubDataTable
        title="Uninvoiced open sales order lines"
        columns={UNINVOICED_OPEN_SO_COLUMNS}
        rows={reportRows}
        rowKey={(row) => `${row.order_number}:${row.sku}:${row.uninvoiced_quantity}:${row.unit_price}`}
        empty="No uninvoiced lines."
      />
    </div>
  );
}

function parseActiveTab(searchParams: URLSearchParams): ReportTabId {
  const raw = searchParams.get("tab");
  if (raw === "uninvoiced-open-sales-orders") {
    return raw;
  }
  return REPORT_TABS[0].id;
}

export function AccountingReportsPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseActiveTab(searchParams);

  function selectTab(tabId: ReportTabId): void {
    setSearchParams({ tab: tabId }, { replace: true });
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-4 md:p-6">
      <PageTitleWithHelp hoverHint={HELP_TEXT}>Accounting reports</PageTitleWithHelp>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2">
        {REPORT_TABS.map((tab) => {
          const selected = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                selected
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === "uninvoiced-open-sales-orders" ? <UninvoicedOpenSalesOrdersReportPanel /> : null}
    </div>
  );
}
